import React, {Component} from 'react';
import {connect} from 'react-redux';
import {withTranslation} from 'react-i18next';
import {Alert, Button, Dialog} from '@betareader/design-system';
import {toast} from 'react-toastify';
import BlockUserButton from '../../components/user/blockUserButton';
import {useFeatureFlags} from '../../hooks/useFeatureFlags';

const userIdOf = user => (typeof user === 'string' ? user : user?._id);

export class ReportOrBlockControl extends Component {
  constructor(props) {
    super(props);
    const firstMessage = this.reportableMessages(props)[0];
    this.state = {
      open: false,
      messageId: firstMessage && firstMessage._id,
      reason: 'scam',
      comment: '',
      submitting: false,
      error: undefined
    };
  }

  reportableMessages = props => {
    const {currentConversation, userProfile} = props;
    return (currentConversation?.messages || []).filter(
      message => userIdOf(message.from) !== userProfile?._id
    );
  };

  selectedMessage = () =>
    this.reportableMessages(this.props).find(
      message => message._id === this.state.messageId
    );

  submitReport = async () => {
    const {currentConversation, idToken, t} = this.props;
    const {messageId, reason, comment} = this.state;
    this.setState({submitting: true, error: undefined});
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/conversations/${currentConversation._id}/reports`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'br-token': idToken
          },
          body: JSON.stringify({messageId, reason, comment})
        }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.msg || t('FailedToSubmitReport'));
      }
      toast.info(t('ReportSubmitted'));
      this.setState({open: false, submitting: false});
    } catch (error) {
      this.setState({submitting: false, error: error.message});
    }
  };

  render() {
    const {featureEnabled, currentConversation, t} = this.props;
    const reportableMessages = this.reportableMessages(this.props);
    if (
      !featureEnabled ||
      !currentConversation?._id ||
      reportableMessages.length === 0
    ) {
      return null;
    }
    const selectedMessage = this.selectedMessage() || reportableMessages[0];
    const reportedUserId = userIdOf(selectedMessage.from);
    const reportedUserName =
      selectedMessage.from?.displayName || t('ConversationMember');
    const {open, messageId, reason, comment, submitting, error} = this.state;

    return (
      <React.Fragment>
        <Button
          type='button'
          variant='secondary'
          size='small'
          aria-label={t('ReportOrBlock')}
          onClick={() => this.setState({open: true})}
        >{t('ReportOrBlock')}</Button>
        <Dialog
          open={open}
          title={t('ReportOrBlock')}
          onClose={() => this.setState({open: false, error: undefined})}
          actions={
            <>
              <Button type='button' variant='secondary'
                onClick={() => this.setState({open: false, error: undefined})}>
                {t('Cancel')}
              </Button>
              <Button type='button' variant='danger' aria-busy={submitting}
                disabled={!messageId || submitting} onClick={this.submitReport}>
                {t('SubmitReport')}
              </Button>
            </>
          }
        >
            <p>{t('ReportPrivacyNotice')}</p>
            {error && <Alert variant='error'>{error}</Alert>}
            <div style={{display: 'grid', gap: 12}}>
              <label className='br-field'>
                <span className='br-field__label'>{t('MessageToReport')}</span>
                <select className='br-field__input'
                value={messageId}
                  onChange={event => this.setState({messageId: event.target.value})}
                >
                  {reportableMessages.map((message, index) => (
                    <option key={message._id} value={message._id}>
                      {`${t('MessageFrom')} ${message.from?.displayName || t('ConversationMember')} #${reportableMessages.length - index}`}
                    </option>
                  ))}
                </select>
              </label>
              <label className='br-field'>
                <span className='br-field__label'>{t('Reason')}</span>
                <select className='br-field__input'
                value={reason}
                  onChange={event => this.setState({reason: event.target.value})}
                >
                  {['scam', 'spam', 'harassment', 'other'].map(value => (
                    <option key={value} value={value}>{t(`ReportReason_${value}`)}</option>
                  ))}
                </select>
              </label>
              <label className='br-field'>
                <span className='br-field__label'>{t('OptionalDetails')}</span>
                <textarea className='br-field__input'
                maxLength={1000}
                value={comment}
                  onChange={event => this.setState({comment: event.target.value})}
                />
              </label>
            </div>
            <div style={{marginTop: '1em'}}>
              <BlockUserButton
                userId={reportedUserId}
                displayName={reportedUserName}
                basic
              />
            </div>
        </Dialog>
      </React.Fragment>
    );
  }
}

const FeatureAwareReportOrBlock = props => {
  const {flags} = useFeatureFlags();
  const featureEnabled = !!(
    flags?.moderation_reporting ||
    (flags?.moderation_reporting_beta_access_allowed &&
      props.userProfile?.betaFeatures?.moderation_reporting)
  );
  return (
    <ReportOrBlockControl {...props} featureEnabled={featureEnabled} />
  );
};

export default withTranslation()(
  connect(state => ({
    idToken: state.user.idToken,
    userProfile: state.user.userProfile,
    currentConversation: state.message.currentConversation
  }))(FeatureAwareReportOrBlock)
);
